import '/assets/styles/pages/checkout-ticketselection.scss';
import { useState, useMemo, useEffect, useRef } from 'react';
import omit from 'lodash/omit';

import config from '@wearetla/tla-essentials-tools/config';

// Functions
import formatDate from '/functions/format-date';
import isExact from '@wearetla/tla-essentials-tools/functions/is-exact';
import uid from '@wearetla/tla-essentials-tools/functions/uid';

// Hooks
import useDebounce from '@wearetla/tla-essentials-tools/hooks/debounce';

// Partials
import { Form } from '/views/partials/forms';
import Img from '@wearetla/tla-essentials-tools/partials/img';
import Icon from '@wearetla/tla-essentials-tools/partials/icon';
import Loader from '/views/partials/loader';
import Btn from '/views/partials/btn';
import Pricebox from '/views/partials/pricebox';

// Context
import { useHead } from '@wearetla/tla-essentials-tools/utilities/head';
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';
import { useParams, useNavigator } from '@wearetla/tla-essentials-tools/utilities/navigator';
import { useAuth } from '@wearetla/tla-essentials-tools/utilities/auth';
import { useGlobalEvents } from '@wearetla/tla-essentials-tools/utilities/global-events';
import { useGlobalState } from '@wearetla/tla-essentials-tools/utilities/global-state';
import { useTranslation } from '/utilities/i18n';

// Services
import eventServices from '/services/event';
import bookingServices from '/services/booking';
import { EventTicketSelector } from '/views/partials/event-ticket-selector';

const parseHead = (event, t) => {
	if (event?.calendar?.full_date) {
		const eventDate = formatDate(event.calendar.full_date, 'd MMMM');
		return {
			title: t('Bilet Seçin - {{eventTitle}} / {{eventDate}} {{eventLocation}}', {
				eventTitle: event.title,
				eventDate,
				eventLocation: event.calendar.event_location,
			}),
			description: t('{{eventDate}} tarihli {{eventLocation}} {{eventTitle}} etkinliği için bilet alın.', {
				eventDate,
				eventLocation: event.calendar.event_location,
				eventTitle: event.title,
			}),
			image: event.image?.asset_paths?.original,
		};
	}

	return {
		title: t('Bilet Seçin'),
	};
};

const getCartPersistenceData = () => {
	try {
		const parsedData = JSON.parse(
			window.localStorage.getItem(config.checkout.cartPersistenceKey)
		);

		if (typeof parsedData === 'object' && Array.isArray(parsedData)) {
			return parsedData;
		}
	} catch (e) {
		console.log('Warning: Invalid synced tickets ref.');
	}
	return [];
};

const retreivePersistentCart = (newEventData, t) => {
	try {
		const persistenceSlots = getCartPersistenceData();
		const seatPlan = newEventData.calendar.venue_seat_plan?.plan_data;

		if(persistenceSlots?.length) {
			const selectedTickets = newEventData.calendar.options.reduce((newSelectedTickets, option) => {
				const matchingSlots = persistenceSlots.reduce((newMatchingSlots, slotId) => {
					const matchingSlot = option.slots.find(slot => slot.status === 1 && slot.id === slotId);

					return [
						...newMatchingSlots,
						...(matchingSlot ? [matchingSlot] : []),
					]
				}, []);
		
				if(matchingSlots?.length) {
					return [
						...newSelectedTickets,
						{
							uid: uid(),
							option: option,
							slots: matchingSlots.map(slot => {
								const slotLabel = (() => {
									if(slot.is_multiple_buy) {
										return t('Numarasız');
									}

									const [blockNumber, rowNumber, seatNumber] = slot.title.split('-');
	
									const block = seatPlan[parseInt(blockNumber) - 1];

									if(block && blockNumber && rowNumber && seatNumber) {
										const blockLabel = `${block.title}, `;
										const rowLabel = block.rowType === 'alphabetical' ? block.rowLettering[parseInt(rowNumber) - 1] : rowNumber;
	
										return `${blockLabel}${rowLabel}-${seatNumber}`;
									}
									else {
										return t('Blok {{blockNumber}}, {{rowNumber}}-{{seatNumber}}', {
											blockNumber,
											rowNumber,
											seatNumber,
										});
									}
	
								})()

								return {
									...slot,
									label: slotLabel,
								}
							}),
						}
					]
				}
		
				return newSelectedTickets;
			}, [])

			return selectedTickets;
		}
	}
	catch(e) {
		console.error('Cart persistence error:', e);
	}

	return [];
}

const CheckoutTicketSelection = () => {
	const { t } = useTranslation();
	const { setHead } = useHead();
	const { triggerEvent } = useGlobalEvents();
	const { setNotFound } = useGlobalState();
	const { loggedIn } = useAuth();
	const { openModal } = useModals();
	const { redirect } = useNavigator();

	const params = useParams(['checkout']);

	const [event, setEvent] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [selectedTickets, setSelectedTickets] = useState(null);

	const eventParams = useMemo(
		() => ({ eventId: params.id, calendarId: params.calendarId }),
		[params]
	);

	const debouncedEventParams = useDebounce(eventParams, 150, null);

	const { summary, canProceedCheckout, selectedSlotIds }  = useMemo(() => {
		const { items, serviceFee, productTotal, selectedSlotIds } = (selectedTickets ?? []).filter(ticket => !!ticket.option).reduce((iterativeSummary, ticket) => {
			const ticketItems = ticket.slots.map((slot) => ({
				title: `${ticket.option.title} - ${slot.label}`,
				price: ticket.option.price,
			}));

			return {
				items: [
					...(iterativeSummary.items ?? []),
					...ticketItems,
				],
				selectedSlotIds: [
					...(iterativeSummary.selectedSlotIds ?? []).sort(),
					...ticket.slots.map(slot => slot.id),
				],
				productTotal: iterativeSummary.productTotal + (ticket.option.price * ticketItems.length),
				serviceFee: iterativeSummary.serviceFee + (ticket.option.service_fee * ticketItems.length),
			}

		}, {
			items: null,
			selectedSlotIds: null,
			productTotal: 0,
			serviceFee: 0,
		})

		return {
			summary: {
				items,
				productTotal,
				serviceFee,
				total: productTotal + serviceFee,
			},
			canProceedCheckout: !!items?.length,
			selectedSlotIds,
		}
	}, [selectedTickets, event]);

	const submit = () => {
		if (loggedIn) {
			setSubmitting(true);

			bookingServices
				.prepareBooking(
					event.id,
					event.calendar.id,
					selectedTickets.reduce((slots, ticket) => ([
						...slots,
						...ticket.slots.map(slot => ({ id: slot.id }))
					]), [])
				)
				.then((payload) => {
					window.localStorage.setItem(config.checkout.salePersistenceKey, payload.id);

					triggerEvent('beginCheckout', { sale: payload });
					redirect('payment');
				})
				.catch((feedback) => {
					openModal('message', { feedback });
					setSubmitting(false);
				});
		} else {
			openModal('auth');
		}
	};

	useCartAnalytics(event, selectedSlotIds);

	useEffect(() => {
		if (debouncedEventParams) {
			const { eventId, calendarId } = debouncedEventParams;
			if (eventId && calendarId) {
				window.localStorage.removeItem(config.checkout.salePersistenceKey);

				if (
					event?.id.toString() !== eventId.toString() ||
					event?.calendar?.id?.toString?.() !== calendarId
				) {
					eventServices
						.getEvent(eventId)
						.then((payload) => {
							const foundCalendar = payload.calendars?.find?.(
								(c) => c.id.toString() === calendarId
							);

							if (foundCalendar) {
								if (foundCalendar.buy_btn_link) {
									redirect(foundCalendar.buy_btn_link, {}, { raw: true, hard: true });
								} else {
									window.localStorage.setItem(config.checkout.checkoutEventPersistenceKey, eventId);
									window.localStorage.setItem(
										config.checkout.checkoutCalendarPersistenceKey,
										calendarId
									);

									const newEventData = {
										...omit(payload, ['calendars']),
										calendar: foundCalendar,
										dateTimeString: formatDate(foundCalendar.full_date, 'dd.MM.y - HH:mm'),
									};

									setSelectedTickets(retreivePersistentCart(newEventData, t));

									setEvent(newEventData);
								}
							} else {
								window.localStorage.removeItem(config.checkout.checkoutEventPersistenceKey);
								window.localStorage.removeItem(config.checkout.checkoutCalendarPersistenceKey);
								openModal('message', { message: t('Bilet bulunamadı') });
								redirect('home');
							}
						})
						.catch(() => {
							setNotFound();
						});
				}
			} else {
				const storageEventId = window.localStorage.getItem(
					config.checkout.checkoutEventPersistenceKey
				);
				const storageCalendarId = window.localStorage.getItem(
					config.checkout.checkoutCalendarPersistenceKey
				);

				if (storageEventId && storageCalendarId) {
					redirect('checkout', { id: storageEventId, calendarId: storageCalendarId });
				} else {
					openModal('message', { message: t('Bilet bulunamadı') });
					redirect('home');
				}
			}
		}
	}, [event, debouncedEventParams, t]);

	useEffect(() => {
		if (event) {
			setHead(parseHead(event, t));
		}
	}, [event, t]);

	return (
		<Form onSubmit={submit} className="layout-checkout-content wrapper loader-container">
			<Loader dark loading={!(event && selectedTickets?.length)} />
			{!!event && (
				<>
					<div className="checkout-maincontent">
						<main className="checkout-section">
							<div className="checkout-section-header">
								<h1 className="header-title">{t('Bilet Seçimi')}</h1>
							</div>

							{!!event.calendar.hero_message?.length && (
								<div className="ticketselection-heromessage">{event.calendar.hero_message}</div>
							)}

							{selectedTickets !== null &&
								<EventTicketSelector
									event={event}
									selectedTickets={selectedTickets}
									onUpdateSelectedTickets={setSelectedTickets}
									initialOptionId={params.optionId} />
							}

							{/* {event.ticketInfo?.notes && 
								<div
									className="ticketselection-notes"
									dangerouslySetInnerHTML={{__html: event.ticketInfo?.notes}} />
							} */}
						</main>
					</div>

					<aside className="checkout-aside">
						<div className="aside-info">
							<div className="info-event">
								<Img className="event-image" src={event.image?.asset_paths?.thumbnail} />

								<div className="event-text">
									<strong className="event-title">{event.title}</strong>

									<div className="event-field">
										<Icon name="map-pin" /> {event.calendar.event_location}
									</div>

									<div className="event-field">
										<Icon name="map-pin" /> {event.dateTimeString}
									</div>
								</div>
							</div>

							{!!summary.items?.length && (
								<>
									<div className="info-tickets">
										<strong className="tickets-title">{t('Biletleriniz:')}</strong>
										<ul className="tickets-list">
											{summary.items.map((item, nth) => (
												<li className="tickets-ticket" key={nth}>
													<span className="ticket-title">{item.title}</span>
													<Pricebox className="ticket-price" price={item.price} />
												</li>
											))}
										</ul>
									</div>

									<ul className="info-prices">
										<li className="prices-item">
											<span>{t('Bilet Fiyatı')}</span>
											<Pricebox className="item-price" price={summary.productTotal} />
										</li>
										<li className="prices-item">
											<span>{t('İşlem Ücreti')}</span>
											<Pricebox className="item-price" price={summary.serviceFee} />
										</li>
										<li className="prices-item total">
											<span>{t('Toplam')}</span>
											<Pricebox className="item-price" price={summary.total} />
										</li>
									</ul>
								</>
							)}
						</div>

						<div className="aside-controls">
							<Btn
								type="submit"
								loading={submitting}
								disabled={!canProceedCheckout}
								className="controls-cta block big primary"
							>
								{t('Güvenli Ödeme Adımına Geç')}
							</Btn>
						</div>
					</aside>
				</>
			)}
		</Form>
	);
};

const useCartAnalytics = (event, selectedSlotIds) => {
	const { triggerEvent } = useGlobalEvents();
	const syncedTicketsRef = useRef(null);

	const setSyncedTicketsRef = (newData) => {
		syncedTicketsRef.current = newData;
		window.localStorage.setItem(config.checkout.cartPersistenceKey, JSON.stringify(newData));
	};

	// Add to cart / remove from cart analytics
	useEffect(() => {
		if (!syncedTicketsRef.current) {
			syncedTicketsRef.current = getCartPersistenceData();
		}

		if (event?.calendar && selectedSlotIds && !isExact(syncedTicketsRef.current, selectedSlotIds)) {
			try {
				const addedTickets = syncedTicketsRef.current.reduce((addedTickets, curTicketId) => (
					addedTickets.filter((ticketId) => ticketId !== curTicketId)
				), selectedSlotIds);

				const removedTickets = selectedSlotIds.reduce((removedTickets, curTicketId) => (
					removedTickets.filter((removedTicketId) => removedTicketId !== curTicketId)
				), syncedTicketsRef.current);

				if (addedTickets?.length) {
					triggerEvent('addToCart', {
						event,
						options: addedTickets
					});
				}
				if (removedTickets?.length) {
					triggerEvent('removeFromCart', {
						event,
						options: removedTickets,
					});
				}
			} catch (e) {
				console.error(e);
			}

			setSyncedTicketsRef(selectedSlotIds);
		}
	}, [selectedSlotIds, event]);
}

export default CheckoutTicketSelection;
